import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  CreditCard, Plus, Trash2, Edit3, X, Save, Eye,
  Image, Mail, MapPin, Phone, User,
  Briefcase, Building2, Monitor, FileText,
  Sparkles, Award, Package, Target, Video,
} from 'lucide-react';
import {
  Button, MessagePlugin, Input, Switch, Checkbox, Tabs, Textarea, Dialog
} from 'tdesign-react';
import ProductImageUploader from '../../components/ProductImageUploader';
import ProductRichTextEditor from '../../components/ProductRichTextEditor';
import BusinessCardPreview from './components/BusinessCardPreview';
import { DynamicIcon, IconPicker, ColorPickerField } from './components/CommonComponents';
import type {
  CardConfig, ProductItem, CompanyIntro, PositioningItem, BusinessSectionItem, DisplayFields, PhilosophyItem, CoreBusinessItem,
  VisionItem, EnterpriseInfoDTO
} from './types';
import {
  pageContentOptions, displayFieldOptions, cardLayouts,
  COMPANY_TYPE_OPTIONS, defaultDisplayFields, defaultContact, defaultCompanyIntro,
  defaultCard, initialCards, DEFAULT_BACKGROUND_LIST
} from './constants';
import { ImagePreviewProvider, PreviewableImage } from './components/ImagePreview';
import { enterpriseInfoApi, tokenStorage } from '../../api';

const emptyCard = (): CardConfig => ({
  ...defaultCard,
  id: 0,
  createdAt: '',
  contact: { ...defaultContact },
  displayFields: { ...defaultDisplayFields },
  selectedPages: [],
  companyIntro: { ...defaultCompanyIntro },
  philosophyItems: [],
  coreBusinessItems: [],
  productIntro: [],
  visionItems: [],
  promoVideo: ''
} as CardConfig);

const MAX_VISION_ITEMS = 5;

// ============ 字段名映射 ============
const DISPLAY_FIELD_NAMES: Record<keyof DisplayFields, string> = {
  name: 'name', position: 'position', avatar: 'avatar',
  phone: 'phone', email: 'email', company: 'company', companyAddress: 'address'
};

const strToBool = (arr: string[], field: keyof DisplayFields): boolean => arr.includes(DISPLAY_FIELD_NAMES[field]);
const boolToStr = (obj: DisplayFields, field: keyof DisplayFields): string => obj[field] ? DISPLAY_FIELD_NAMES[field] : '';

// ============ API → CardConfig ============
const mapApiToConfig = (api: EnterpriseInfoDTO): CardConfig => {
  const displayFields: DisplayFields = {
    name: strToBool(api.cardDisplayFields, 'name'),
    position: strToBool(api.cardDisplayFields, 'position'),
    avatar: strToBool(api.cardDisplayFields, 'avatar'),
    phone: strToBool(api.cardDisplayFields, 'phone'),
    email: strToBool(api.cardDisplayFields, 'email'),
    company: strToBool(api.cardDisplayFields, 'company'),
    companyAddress: strToBool(api.cardDisplayFields, 'companyAddress'),
  };

  const enterpriseProfile = api.enterpriseProfile || ({} as any);
  const positioning: PositioningItem[] = (enterpriseProfile.positioning || []).map(
    (p: any, i: number) => ({ id: `pos-${i}`, icon: p.icon || 'star', title: p.content || '' })
  );

  return {
    id: api.id || 0,
    name: enterpriseProfile.enterpriseName || '',
    logo: api.logo || '',
    pageBg: api.cardPageBackground || '',
    mainTitle: api.theme || '',
    subTitle: api.subTheme || '',
    subTitleEn: api.subThemeEn || '',
    contact: {
      qrcode: api.contact?.qrCode || '',
      address: api.contact?.address || '',
      phone: api.contact?.phone || '',
      email: api.contact?.email || '',
    },
    displayFields,
    cardBgType: api.cardBackgroundImage ? 'custom' : 'default',
    cardBg: api.cardBackgroundImage || DEFAULT_BACKGROUND_LIST[0],
    cardLayout: api.contentPosition || 'left',
    status: 'active',
    createdAt: '',
    saveToContacts: false,
    enablePoster: false,
    posterBg: '',
    posterCardPosition: { x: 50, y: 50 },
    posterInvitePosition: { x: 50, y: 50 },
    enableMiniCode: false,
    enableShortLink: false,
    selectedPages: api.customDisplayConfig || [],
    philosophyItems: (api.enterprisePhilosophy || []).map((p: any, i: number) => ({
      id: `phil-${i}`, icon: p.icon || 'star', iconBgColor: p.bgColor || '#1c5bff',
      title: p.title || '', titleEn: p.titleEn || '', description: p.description || ''
    })),
    coreBusinessItems: (api.coreBusiness || []).map((b: any, i: number) => ({
      id: `cb-${i}`, title: b.title || '', description: b.description || ''
    })),
    productIntro: (api.productIntros || []).map((p: any, i: number) => ({
      id: `prod-${i}`, image: p.image || '', title: p.title || '', description: p.description || ''
    })),
    companyIntro: {
      companyName: enterpriseProfile.enterpriseName || '',
      companyTypes: enterpriseProfile.enterpriseType ? enterpriseProfile.enterpriseType.split(',').filter(Boolean) : [],
      foundedDate: enterpriseProfile.establishedTime || '',
      registeredCapital: enterpriseProfile.registeredCapital || '',
      creditRating: enterpriseProfile.creditRating || '',
      employeeCount: enterpriseProfile.employeeCount || '',
      description: enterpriseProfile.description || '',
      companyPositioning: positioning,
      businessSectors: (enterpriseProfile.businessSectors || []).map((s: any, i: number) => ({
        icon: s.icon || 'layers', title: s.title || ''
      })),
    },
    companyQualifications: {
      honors: [],
      businessQualifications: [],
      strength: [],
      endorsement: []
    },
    investmentProjectItems: [],
    companyStrengthItems: [],
    cooperation: { count: 0, logos: [] },
    companyPromo: { images: [], videos: [] },
    visionItems: (api.enterpriseVision || []).map((v: any, i: number) => ({
      id: `vis-${i}`, time: v.time || '', goalTitle: v.goalTitle || ''
    })),
    promoVideo: api.promoVideo || '',
  };
};

// ============ CardConfig → API ============
const removeEmpty = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== '' && value !== null && value !== undefined) {
      result[key] = value;
    }
  }
  return result;
};

const mapConfigToApi = (card: CardConfig): any => {
  const cardDisplayFields = (['name','position','avatar','phone','email','company','companyAddress'] as (keyof DisplayFields)[])
    .map(f => boolToStr(card.displayFields, f))
    .filter(Boolean);

  const enterpriseProfile = card.companyIntro || ({} as CompanyIntro);
  const positioning = (enterpriseProfile.companyPositioning || [])
    .filter(p => p.title || p.icon)
    .map(p => removeEmpty({ content: p.title, icon: p.icon }));

  // 构建结果，过滤空值
  const result: Record<string, any> = {};

  if (card.id) result.id = card.id;
  if (card.cardBg) result.cardBackgroundImage = card.cardBg;
  result.cardDisplayFields = cardDisplayFields;
  if (card.pageBg) result.cardPageBackground = card.pageBg;

  const contact = removeEmpty({
    qrCode: card.contact.qrcode,
    address: card.contact.address,
    email: card.contact.email,
    phone: card.contact.phone,
  });
  if (Object.keys(contact).length > 0) result.contact = contact;

  if (card.cardLayout) result.contentPosition = card.cardLayout;

  const coreBusiness = (card.coreBusinessItems || [])
    .filter(b => b.title || b.description)
    .map(b => removeEmpty({ title: b.title, description: b.description }));
  if (coreBusiness.length > 0) result.coreBusiness = coreBusiness;

  if (card.selectedPages && card.selectedPages.length > 0) result.customDisplayConfig = card.selectedPages;

  const philosophy = (card.philosophyItems || [])
    .filter(p => p.title || p.description || p.icon)
    .map(p => removeEmpty({ bgColor: p.iconBgColor, description: p.description, icon: p.icon, title: p.title, titleEn: p.titleEn }));
  if (philosophy.length > 0) result.enterprisePhilosophy = philosophy;

  const businessSectors = (enterpriseProfile.businessSectors || [])
    .filter(s => s.title)
    .map(s => removeEmpty({ icon: s.icon, title: s.title }));

  const profile = removeEmpty({
    creditRating: enterpriseProfile.creditRating,
    employeeCount: enterpriseProfile.employeeCount,
    enterpriseName: enterpriseProfile.companyName,
    enterpriseType: (enterpriseProfile.companyTypes || []).join(',') || '',
    establishedTime: enterpriseProfile.foundedDate,
    registeredCapital: enterpriseProfile.registeredCapital,
    description: enterpriseProfile.description,
  });
  if (positioning.length > 0) profile.positioning = positioning;
  if (businessSectors.length > 0) profile.businessSectors = businessSectors;
  if (Object.keys(profile).length > 0) result.enterpriseProfile = profile;

  const vision = (card.visionItems || [])
    .filter(v => v.time || v.goalTitle)
    .map(v => removeEmpty({ time: v.time, goalTitle: v.goalTitle }));
  if (vision.length > 0) result.enterpriseVision = vision;

  if (card.logo) result.logo = card.logo;

  const products = (card.productIntro || [])
    .filter(p => p.image || p.title || p.description)
    .map(p => removeEmpty({ description: p.description, image: p.image, title: p.title }));
  if (products.length > 0) result.productIntros = products;

  if (card.promoVideo) result.promoVideo = card.promoVideo;
  if (card.subTitle) result.subTheme = card.subTitle;
  if (card.subTitleEn) result.subThemeEn = card.subTitleEn;
  if (card.mainTitle) result.theme = card.mainTitle;

  return result;
};

function SectionCard({
  title,
  icon: Icon,
  children,
  action
}: {
  title: React.ReactNode;
  icon: any;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#1c5bff]" />
          {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function DisplayRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-800 font-medium text-right max-w-[60%] truncate">{value || '-'}</span>
    </div>
  );
}

function CardLayoutPreview({ layout, selected }: { layout: string; selected: boolean }) {
  const borderColor = selected ? '#1c5bff' : '#e5e7eb';
  const bgColor = selected ? '#eff6ff' : '#ffffff';

  const renderPreview = () => {
    switch (layout) {
      case 'left':
        return (
          <div className="h-full flex flex-col">
            <div className="h-5 bg-gradient-to-r from-blue-500 to-blue-400 flex items-center px-2.5">
              <div className="h-2 w-12 bg-white/80 rounded" />
            </div>
            <div className="flex-1 flex items-center gap-2 px-2.5">
              <div className="flex flex-col gap-1 flex-1">
                <div className="flex gap-1">
                  <div className="h-2 w-10 bg-blue-400 rounded" />
                  <div className="h-2 w-8 bg-gray-200 rounded" />
                </div>
                <div className="h-1 w-full bg-gray-100 rounded" />
                <div className="h-1 w-4/5 bg-gray-100 rounded" />
              </div>
            </div>
            <div className="h-3 bg-gray-50 px-2.5 flex items-center">
              <div className="h-1 w-full bg-gray-200 rounded" />
            </div>
          </div>
        );
      case 'center':
        return (
          <div className="h-full flex flex-col">
            <div className="flex flex-1 p-2.5 gap-2">
              <div className="flex flex-col gap-1 flex-1">
                <div className="h-2 w-12 bg-blue-400 rounded" />
                <div className="h-1 w-14 bg-gray-200 rounded" />
                <div className="h-1 w-8 bg-gray-100 rounded" />
              </div>
            </div>
            <div className="flex flex-col border-t border-gray-100 px-2 py-1.5">
              <div className="h-1 w-full bg-gray-200 rounded" />
              <div className="h-1 w-full bg-gray-200 rounded mt-1" />
              <div className="h-1 w-full bg-gray-200 rounded mt-1" />
            </div>
          </div>
        );
      case 'right':
        return (
          <div className="h-full flex flex-col py-2">
            <div className='w-full flex items-center justify-center'>
              <div className="flex flex-col gap-1">
                <div className="h-1 w-12 bg-blue-400 rounded" />
                <div className="h-1 w-8 bg-gray-200 rounded" />
                <div className="h-1 w-10 bg-gray-100 rounded" />
              </div>
            </div>
            <div className="flex flex-col gap-1 mt-2 pt-2 border-t border-gray-100 w-full px-2">
              <div className="h-1 w-10 bg-gray-200 rounded mx-auto" />
              <div className="h-1 w-10 bg-gray-200 rounded mx-auto" />
              <div className="h-1 w-10 bg-gray-200 rounded mx-auto" />
            </div>
          </div>
        );
      case 'preview-4':
        return (
          <div className="h-full flex">
            <div className="flex-1 flex flex-col justify-center px-2.5 gap-1">
              <div className="h-2 w-14 bg-gray-700 rounded" />
              <div className="h-1 w-8 bg-gray-500 rounded" />
              <div className="h-1 w-6 bg-gray-400 rounded" />
              <div className="mt-1 h-1 w-full bg-gray-200 rounded" />
              <div className="h-1 w-10 bg-gray-200 rounded" />
              <div className="h-1 w-9 bg-gray-200 rounded" />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="border-2 rounded-lg cursor-pointer transition-all p-2"
      style={{ borderColor, backgroundColor: bgColor }}
    >
      <div className="h-20 overflow-hidden rounded border border-gray-200 bg-white">
        {renderPreview()}
      </div>
      <div className="text-sm text-center mt-1.5 font-medium" style={{ color: selected ? '#1c5bff' : '#374151' }}>
        {cardLayouts.find(l => l.value === layout)?.label}
      </div>
    </div>
  );
}

export default function ElectronicBusinessCards() {
  const [card, setCard] = useState<CardConfig>(initialCards[0] || emptyCard());
  const [isEditing, setIsEditing] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewConfig, setPreviewConfig] = useState<CardConfig | null>(null);
  const [customBgList, setCustomBgList] = useState<string[]>([]);
  const initializedRef = useRef(false);

  const tenantInfo = tokenStorage.getTenantInfo();
  const tenantLevel = tenantInfo?.tenantLevel;
  const showProductIntro = tenantLevel === 4 || tenantLevel === 5 || tenantLevel === 6;
  const showCompanyPromo = tenantLevel === 5 || tenantLevel === 6;

  // 根据tenantLevel动态过滤页面内容选项
  const availablePageOptions = pageContentOptions.filter(o => {
    if (o.value === 'productIntro') return showProductIntro;
    if (o.value === 'companyPromo') return showCompanyPromo;
    return true;
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await enterpriseInfoApi.getInfo();
      if (res.code === 200 && res.data) {
        console.log('API 返回数据:', res.data);
        // 类型转换：API 返回 EnterpriseInfoVO，转为内部使用的 EnterpriseInfoDTO
        const dtoData = res.data as unknown as EnterpriseInfoDTO;
        const mappedConfig = mapApiToConfig(dtoData);
        setCard(mappedConfig);
        // 初始化自定义背景图列表：如果当前背景图不在预设列表中，将其添加到自定义列表
        if (mappedConfig.cardBg && !DEFAULT_BACKGROUND_LIST.includes(mappedConfig.cardBg)) {
          setCustomBgList([mappedConfig.cardBg]);
        }
        setHasData(true);
        setIsEditing(false);
      } else {
        setCard(emptyCard());
        setCustomBgList([]);
        setHasData(false);
        setIsEditing(true);
      }
    } catch (err) {
      setCard(emptyCard());
      setCustomBgList([]);
      setHasData(false);
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    loadData();
  }, [loadData]);

  const toggleDisplayField = (field: keyof DisplayFields) => {
    if (field === 'name') return;
    setCard(prev => ({
      ...prev,
      displayFields: { ...prev.displayFields, [field]: !prev.displayFields[field] }
    }));
  };

  const togglePageContent = (pageValue: string) => {
    setCard(prev => {
      const pages = prev.selectedPages || [];
      if (pages.includes(pageValue)) {
        const newPages = pages.filter(p => p !== pageValue);
        if (activeTab === pageValue && newPages.length > 0) {
          setActiveTab(newPages[0]);
        }
        return { ...prev, selectedPages: newPages };
      } else {
        const newPages = [...pages, pageValue];
        if (pages.length === 0) {
          setActiveTab(pageValue);
        }
        return { ...prev, selectedPages: newPages };
      }
    });
  };

  const handleTabDragSort = (context: { current: string; target: string; currentIndex: number; targetIndex: number }) => {
    setCard(prev => {
      const pages = [...(prev.selectedPages || [])];
      const { currentIndex, targetIndex } = context;
      if (currentIndex === targetIndex) return prev;
      const [movedItem] = pages.splice(currentIndex, 1);
      pages.splice(targetIndex, 0, movedItem);
      return { ...prev, selectedPages: pages };
    });
  };

  const handleTabRemove = (options: { value: string; index: number }) => {
    setCard(prev => {
      const pages = [...(prev.selectedPages || [])];
      pages.splice(options.index, 1);
      return { ...prev, selectedPages: pages };
    });
  };

  // ============ 企业简介 ============
  const updateCompanyIntro = (updates: Partial<CompanyIntro>) => {
    setCard(prev => ({ ...prev, companyIntro: { ...prev.companyIntro, ...updates } }));
  };

  const addBusinessSectionItem = () => {
    const newItem: BusinessSectionItem = { id: Date.now().toString(), icon: 'layers', title: '' };
    setCard(prev => ({
      ...prev,
      companyIntro: {
        ...prev.companyIntro,
        businessSectors: [...(prev.companyIntro?.businessSectors || []), newItem]
      }
    }));
  };

  const removeBusinessSectionItem = (index: number) => {
    let newBusinessSectors = [...(card.companyIntro?.businessSectors || [])];
    newBusinessSectors.splice(index, 1);
    setCard(prev => ({
      ...prev,
      companyIntro: {
        ...prev.companyIntro,
        businessSectors: newBusinessSectors
      }
    }));
  };

  const updateBusinessSectionItem = (id: string, updates: Partial<BusinessSectionItem>) => {
    setCard(prev => ({
      ...prev,
      companyIntro: {
        ...prev.companyIntro,
        businessSectors: (prev.companyIntro?.businessSectors || []).map(item => item.id === id ? { ...item, ...updates } : item)
      }
    }));
  };

  // ============ 核心业务 ============
  const addCoreBusinessItem = () => {
    const newItem: CoreBusinessItem = { id: Date.now().toString(), title: '', description: '' };
    setCard(prev => ({ ...prev, coreBusinessItems: [...(prev.coreBusinessItems || []), newItem] }));
  };

  const removeCoreBusinessItem = (id: string) => {
    setCard(prev => ({ ...prev, coreBusinessItems: (prev.coreBusinessItems || []).filter(item => item.id !== id) }));
  };

  const updateCoreBusinessItem = (id: string, updates: Partial<CoreBusinessItem>) => {
    setCard(prev => ({ ...prev, coreBusinessItems: (prev.coreBusinessItems || []).map(item => item.id === id ? { ...item, ...updates } : item) }));
  };

  // ============ 产品介绍 ============
  const addProductItem = () => {
    const newItem: ProductItem = { id: Date.now().toString(), image: '', title: '', description: '' };
    setCard(prev => ({ ...prev, productIntro: [...(prev.productIntro || []), newItem] }));
  };

  const removeProductItem = (id: string) => {
    setCard(prev => ({ ...prev, productIntro: (prev.productIntro || []).filter(item => item.id !== id) }));
  };

  const updateProductItem = (id: string, updates: Partial<ProductItem>) => {
    setCard(prev => ({ ...prev, productIntro: (prev.productIntro || []).map(item => item.id === id ? { ...item, ...updates } : item) }));
  };

  // ============ 企业愿景 ============
  const addVisionItem = () => {
    const currentItems = card.visionItems || [];
    if (currentItems.length >= MAX_VISION_ITEMS) {
      MessagePlugin.warning(`最多只能添加 ${MAX_VISION_ITEMS} 个愿景节点`);
      return;
    }
    const newItem: VisionItem = { id: Date.now().toString(), time: '', goalTitle: '' };
    setCard(prev => ({ ...prev, visionItems: [...(prev.visionItems || []), newItem] }));
  };

  const removeVisionItem = (id: string) => {
    setCard(prev => ({ ...prev, visionItems: (prev.visionItems || []).filter(item => item.id !== id) }));
  };

  const updateVisionItem = (id: string, updates: Partial<VisionItem>) => {
    setCard(prev => ({ ...prev, visionItems: (prev.visionItems || []).map(item => item.id === id ? { ...item, ...updates } : item) }));
  };

  // ============ 企业理念 ============
  const addPhilosophyItem = () => {
    const newItem: PhilosophyItem = {
      id: Date.now().toString(),
      icon: 'sparkles',
      iconBgColor: '#1c5bff',
      title: '',
      titleEn: '',
      description: ''
    };
    setCard(prev => ({
      ...prev,
      philosophyItems: [...(prev.philosophyItems || []), newItem]
    }));
  };

  const removePhilosophyItem = (id: string) => {
    setCard(prev => ({
      ...prev,
      philosophyItems: (prev.philosophyItems || []).filter(item => item.id !== id)
    }));
  };

  const updatePhilosophyItem = (id: string, updates: Partial<PhilosophyItem>) => {
    setCard(prev => ({
      ...prev,
      philosophyItems: (prev.philosophyItems || []).map(item =>
        item.id === id ? { ...item, ...updates } : item
      )
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const apiData = mapConfigToApi(card);
      const res = await enterpriseInfoApi.updateInfo(apiData);
      if (res.code === 200) {
        MessagePlugin.success(card.id ? '更新成功' : '保存成功');
        setIsEditing(false);
        setHasData(true);
        if (res.data) {
          setCard(mapApiToConfig(res.data as unknown as EnterpriseInfoDTO));
        } else {
          await loadData();
        }
      } else {
        MessagePlugin.error(res.message || '保存失败');
      }
    } catch (err: any) {
      MessagePlugin.error(err?.message || '保存失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasData) {
      setIsEditing(false);
      loadData();
    } else {
      loadData();
    }
  };

  // 预览时直接使用当前card配置
  const handlePreview = () => {
    setPreviewConfig(card);
    setPreviewVisible(true);
  };

  const selectBg = (bgUrl: string) => {
    setCard(prev => ({ ...prev, cardBgType: 'custom', cardBg: bgUrl }));
  };

  const handleCustomBgUpload = (url: string) => {
    if (url) {
      setCustomBgList(prev => [...prev.filter(u => u !== url), url]);
      selectBg(url);
    }
  };

  const removeCustomBg = (url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setCustomBgList(prev => prev.filter(u => u !== url));
    if (card.cardBg === url) {
      selectBg(DEFAULT_BACKGROUND_LIST[0]);
    }
  };

  // ============ Tabs 内容渲染 ============
  const renderTabPanelContent = (pageValue: string) => {
    const option = pageContentOptions.find(o => o.value === pageValue);
    if (!option) return null;
    const IconComp = option.icon;

    if (pageValue === 'companyIntro') {
      const intro = card.companyIntro || ({} as CompanyIntro);
      return (
        <div className="p-3 bg-gray-50 rounded-lg max-h-[520px] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <IconComp className="w-4 h-4 text-[#1c5bff]" />
              <span className="text-sm font-medium text-gray-800">{option.label}配置</span>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-gray-500 block mb-1">企业名称</label>
                <Input
                  value={intro.companyName || ''}
                  onChange={(v) => updateCompanyIntro({ companyName: v })}
                  placeholder="请输入企业名称"
                  size="small"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500 block mb-1">企业类型（勾选或手输，逗号分隔）</label>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {COMPANY_TYPE_OPTIONS.map(t => {
                      const checked = (intro.companyTypes || []).includes(t);
                      return (
                        <label
                          key={t}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs cursor-pointer transition-colors ${
                            checked
                              ? 'border-[#1c5bff] bg-blue-50 text-[#1c5bff]'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="w-3 h-3 accent-[#1c5bff]"
                            checked={checked}
                            onChange={(e) => {
                              const current = intro.companyTypes || [];
                              if (e.target.checked) {
                                updateCompanyIntro({ companyTypes: [...current, t] });
                              } else {
                                updateCompanyIntro({ companyTypes: current.filter(x => x !== t) });
                              }
                            }}
                          />
                          {t}
                        </label>
                      );
                    })}
                  </div>
                  <Input
                    value={(intro.companyTypes || []).filter(t => !COMPANY_TYPE_OPTIONS.includes(t)).join(',')}
                    onChange={(v) => {
                      const customTypes = v.split(/[,，]/).map(s => s.trim()).filter(Boolean);
                      const presetTypes = (intro.companyTypes || []).filter(t => COMPANY_TYPE_OPTIONS.includes(t));
                      updateCompanyIntro({ companyTypes: [...presetTypes, ...customTypes] });
                    }}
                    placeholder="手动输入，多个用英文逗号分隔"
                    size="small"
                  />
                  {(intro.companyTypes || []).length > 0 && (
                    <div className="text-[10px] text-gray-400">
                      已选：{(intro.companyTypes || []).join('、')}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">成立时间</label>
                <select
                  value={intro.foundedDate || ''}
                  onChange={(e) => updateCompanyIntro({ foundedDate: e.target.value })}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:border-[#1c5bff] focus:outline-none bg-white"
                >
                  <option value="">请选择年份</option>
                  {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <option key={year} value={String(year)}>{year}年</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">注册资本</label>
                <Input
                  value={intro.registeredCapital || ''}
                  onChange={(v) => updateCompanyIntro({ registeredCapital: v })}
                  placeholder="例如：1000万元"
                  size="small"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">信用评级</label>
                <Input
                  value={intro.creditRating || ''}
                  onChange={(v) => updateCompanyIntro({ creditRating: v })}
                  placeholder="例如：AAA"
                  size="small"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">员工人数</label>
                <Input
                  value={intro.employeeCount || ''}
                  onChange={(v) => updateCompanyIntro({ employeeCount: v })}
                  placeholder="例如：500人"
                  size="small"
                />
              </div>
            </div>
            {/* 企业定位 */}
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-500">企业定位（可多个，带图标）</label>
                <Button
                  size="small"
                  theme="primary"
                  variant="outline"
                  onClick={() => {
                    const newItem: PositioningItem = {
                      id: Date.now().toString(),
                      icon: '⭐',
                      title: ''
                    };
                    updateCompanyIntro({ companyPositioning: [...(intro.companyPositioning || []), newItem] });
                  }}
                >
                  <span className="flex items-center gap-1">
                    <Plus className="w-3 h-3" />
                    添加定位
                  </span>
                </Button>
              </div>

              {(intro.companyPositioning || []).length === 0 ? (
                <div className="text-center py-4 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-white">
                  暂无定位，点击"添加定位"按钮新增
                </div>
              ) : (
                <div className="space-y-2">
                  {(intro.companyPositioning || []).map((item, index) => (
                    <div
                      key={item.id}
                      className="bg-white rounded-lg border border-gray-200 p-2 relative group"
                    >
                      <button
                        onClick={() => {
                          const current = intro.companyPositioning || [];
                          updateCompanyIntro({ companyPositioning: current.filter(x => x.id !== item.id) });
                        }}
                        className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                        title="删除"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded-full bg-blue-100 text-[#1c5bff] text-[10px] flex items-center justify-center font-bold flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1 flex items-center gap-2">
                          <div className="flex-shrink-0">
                            <IconPicker
                              value={item.icon}
                              onChange={(v) => {
                                const current = intro.companyPositioning || [];
                                updateCompanyIntro({
                                  companyPositioning: current.map(x =>
                                    x.id === item.id ? { ...x, icon: v } : x
                                  )
                                });
                              }}
                            />
                          </div>
                          <Input
                            value={item.title}
                            onChange={(v) => {
                              const current = intro.companyPositioning || [];
                              updateCompanyIntro({
                                companyPositioning: current.map(x =>
                                  x.id === item.id ? { ...x, title: v } : x
                                )
                              });
                            }}
                            placeholder="定位名称"
                            size="small"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(intro.companyPositioning || []).length > 0 && (
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <div className="text-[10px] text-gray-400 mb-1">已选预览：</div>
                  <div className="flex flex-wrap gap-1">
                    {(intro.companyPositioning || []).map(item => (
                      <div
                        key={item.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded border border-gray-200 text-xs"
                      >
                        <DynamicIcon iconName={item.icon} className="w-3 h-3" />
                        <span>{item.title || '未命名'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* 详细介绍 */}
            <div className="col-span-2">
              <label className="text-xs text-gray-500 block mb-1">详细介绍（富文本）</label>
              <ProductRichTextEditor
                value={intro.description || ''}
                onChange={(v) => updateCompanyIntro({ description: v })}
                showHeight={false}
              />
            </div>
          </div>

          <div className="mt-3 bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-gray-700">主营板块</span>
              <Button size="small" theme="primary" variant="outline" onClick={addBusinessSectionItem}>
                <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
              </Button>
            </div>
            {(intro.businessSectors || []).length === 0 ? (
              <div className="text-center py-4 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg">
                暂无主营板块
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {(intro.businessSectors || []).map((item, idx) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-3 relative group bg-gray-50">
                    <button
                      onClick={() => removeBusinessSectionItem(idx)}
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                      <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                      板块 #{idx + 1}
                    </div>
                    <div className="space-y-2">
                      <IconPicker
                        value={item.icon}
                        onChange={(v) => updateBusinessSectionItem(item.id, { icon: v })}
                        label="图标"
                      />
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">标题</label>
                        <Input
                          value={item.title}
                          onChange={(v) => updateBusinessSectionItem(item.id, { title: v })}
                          placeholder="请输入板块标题"
                          size="small"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (pageValue === 'coreBusiness') {
      return (
        <div className="p-3 bg-gray-50 rounded-lg max-h-[520px] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <IconComp className="w-4 h-4 text-[#1c5bff]" />
              <span className="text-sm font-medium text-gray-800">{option.label}配置</span>
            </div>
            <Button size="small" theme="primary" variant="outline" onClick={addCoreBusinessItem}>
              <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
            </Button>
          </div>
          {(card.coreBusinessItems || []).length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-white">
              暂无内容，点击"添加"按钮新增
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {(card.coreBusinessItems || []).map((item, idx) => (
                <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-3 relative group">
                  <button
                    onClick={() => removeCoreBusinessItem(item.id)}
                    className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                    <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                    业务 #{idx + 1}
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">标题</label>
                      <Input
                        value={item.title}
                        onChange={(v) => updateCoreBusinessItem(item.id, { title: v })}
                        placeholder="请输入标题"
                        size="small"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">描述</label>
                      <Textarea
                        value={item.description}
                        onChange={(v) => updateCoreBusinessItem(item.id, { description: v })}
                        placeholder="请输入描述内容"
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (pageValue === 'companyVision') {
      const visionItemsCount = (card.visionItems || []).length;
      const isMaxVisionReached = visionItemsCount >= MAX_VISION_ITEMS;

      return (
        <div className="p-3 bg-gray-50 rounded-lg max-h-[520px] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <IconComp className="w-4 h-4 text-[#1c5bff]" />
              <span className="text-sm font-medium text-gray-800">{option.label}配置</span>
            </div>
            <div className="flex items-center gap-2">
              {isMaxVisionReached && (
                <span className="text-xs text-orange-500">已达上限 ({MAX_VISION_ITEMS}个)</span>
              )}
              <Button
                size="small"
                theme="primary"
                variant="outline"
                onClick={addVisionItem}
                disabled={isMaxVisionReached}
              >
                <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
              </Button>
            </div>
          </div>
          {visionItemsCount === 0 ? (
            <div className="text-center py-8 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-white">
              暂无内容，点击"添加"按钮新增
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {(card.visionItems || []).map((item, idx) => (
                <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-3 relative group">
                  <button
                    onClick={() => removeVisionItem(item.id)}
                    className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                    <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                    愿景节点 #{idx + 1}
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">时间节点</label>
                    <Input
                      value={item.time}
                      onChange={(v) => updateVisionItem(item.id, { time: v })}
                      placeholder="例如：2025年"
                      size="small"
                    />
                  </div>
                  <div className='mt-3'>
                    <label className="text-xs text-gray-500 block mb-1">阶段目标</label>
                    <Input
                      value={item.goalTitle}
                      onChange={(v) => updateVisionItem(item.id, { goalTitle: v })}
                      placeholder="请输入阶段目标"
                      size="small"
                      style={{ minHeight: '60px' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (pageValue === 'companyPhilosophy') {
      return (
        <div className="p-3 bg-gray-50 rounded-lg max-h-[520px] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <IconComp className="w-4 h-4 text-[#1c5bff]" />
              <span className="text-sm font-medium text-gray-800">{option.label}配置</span>
            </div>
            <Button
              size="small"
              theme="primary"
              variant="outline"
              onClick={addPhilosophyItem}
            >
              <span className="flex items-center gap-1">
                <Plus className="w-3 h-3" />
                添加
              </span>
            </Button>
          </div>

          {(card.philosophyItems || []).length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-white">
              暂无内容，点击"添加"按钮新增
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {(card.philosophyItems || []).map((item, index) => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg border border-gray-200 p-3 relative group"
                >
                  <button
                    onClick={() => removePhilosophyItem(item.id)}
                    className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                    title="删除"
                  >
                    <X className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                    <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">
                      {index + 1}
                    </span>
                    理念 #{index + 1}
                  </div>

                  <div className="space-y-2">
                    <IconPicker
                      value={item.icon}
                      onChange={(v) => updatePhilosophyItem(item.id, { icon: v })}
                      label="图标（必填）"
                    />
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">标题（必填）</label>
                      <Input
                        value={item.title}
                        onChange={(v) => updatePhilosophyItem(item.id, { title: v })}
                        placeholder="请输入标题"
                        size="small"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">英文标题</label>
                      <Input
                        value={item.titleEn}
                        onChange={(v) => updatePhilosophyItem(item.id, { titleEn: v })}
                        placeholder="English Title"
                        size="small"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">描述（必填）</label>
                      <Textarea
                        value={item.description}
                        onChange={(v) => updatePhilosophyItem(item.id, { description: v })}
                        placeholder="请输入描述内容"
                        rows={3}
                      />
                    </div>
                    <ColorPickerField
                      value={item.iconBgColor}
                      onChange={(v) => updatePhilosophyItem(item.id, { iconBgColor: v })}
                      label="背景色"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (pageValue === 'productIntro') {
      return (
        <div className="p-3 bg-gray-50 rounded-lg max-h-[520px] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <IconComp className="w-4 h-4 text-[#1c5bff]" />
              <span className="text-sm font-medium text-gray-800">{option.label}配置</span>
            </div>
            <Button size="small" theme="primary" variant="outline" onClick={addProductItem}>
              <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
            </Button>
          </div>
          {(card.productIntro || []).length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-white">
              暂无内容，点击"添加"按钮新增
            </div>
          ) : (
            <div className="space-y-3">
              {(card.productIntro || []).map((item, idx) => (
                <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-4 relative group">
                  <button
                    onClick={() => removeProductItem(item.id)}
                    className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                    <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                    产品 #{idx + 1}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">产品图片</label>
                      <ProductImageUploader
                        value={item.image}
                        onChange={(v) => updateProductItem(item.id, { image: v as string })}
                        simple
                      />
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">标题</label>
                        <Input
                          value={item.title}
                          onChange={(v) => updateProductItem(item.id, { title: v })}
                          placeholder="请输入产品标题"
                          size="small"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">详细描述（富文本）</label>
                        <ProductRichTextEditor
                          value={item.description}
                          onChange={(v) => updateProductItem(item.id, { description: v })}
                          showHeight={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (pageValue === 'companyPromo') {
      return (
        <div className="p-3 bg-gray-50 rounded-lg max-h-[520px] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <IconComp className="w-4 h-4 text-[#1c5bff]" />
              <span className="text-sm font-medium text-gray-800">{option.label}配置</span>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-gray-500 mb-3">上传企业宣传视频（仅支持单个视频）</div>
            <ProductImageUploader
              value={card.promoVideo || ''}
              onChange={(v) => setCard({ ...card, promoVideo: typeof v === 'string' ? v : '' })}
              uploadType={2}
              simple
            />
            <div className="text-xs text-gray-400 mt-2">支持单个视频上传，大小不超过10MB</div>
          </div>
        </div>
      );
    }

    return null;
  };

  // ============ 渲染编辑/展示模式 ============
  const renderContent = () => {
    if (isEditing) {
      return (
        <div>
          {/* 左侧表单 */}
          <div className="space-y-4">
            {/* 基础信息 */}
            <SectionCard title="基础信息" icon={Image}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm text-gray-500 block mb-2">企业Logo</label>
                  <ProductImageUploader value={card.logo} onChange={(v) => setCard({ ...card, logo: v as string })} simple />
                </div>
                <div>
                  <label className="text-sm text-gray-500 block mb-2">主题</label>
                  <Input value={card.mainTitle} onChange={(v) => setCard({ ...card, mainTitle: v })} placeholder="如：科技有限公司" />
                </div>
                <div>
                  <label className="text-sm text-gray-500 block mb-2">副题</label>
                  <Input value={card.subTitle} onChange={(v) => setCard({ ...card, subTitle: v })} placeholder="中文副标题" />
                </div>
                <div className="col-span-2">
                  <label className="text-sm text-gray-500 block mb-2">副题英文</label>
                  <Input value={card.subTitleEn} onChange={(v) => setCard({ ...card, subTitleEn: v })} placeholder="English subtitle" />
                </div>
                <div className="col-span-2">
                  <label className="text-sm text-gray-500 block mb-2">页面背景大背景图片</label>
                  <ProductImageUploader value={card.pageBg} onChange={(v) => setCard({ ...card, pageBg: v as string })} simple />
                  <div className="text-xs text-gray-400 mt-1">建议使用长图，尺寸比例约为 16:9</div>
                </div>
              </div>
            </SectionCard>

            {/* 联系方式 */}
            <SectionCard title="联系方式" icon={Phone}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm text-gray-500 block mb-2">二维码（电话、邮箱、地址）</label>
                  <ProductImageUploader value={card.contact.qrcode} onChange={(v) => setCard({ ...card, contact: { ...card.contact, qrcode: v as string } })} simple />
                </div>
                <div>
                  <label className="text-sm text-gray-500 block mb-2">企业电话</label>
                  <Input value={card.contact.phone} onChange={(v) => setCard({ ...card, contact: { ...card.contact, phone: v } })} placeholder="400-xxx-xxxx" />
                </div>
                <div>
                  <label className="text-sm text-gray-500 block mb-2">企业邮箱</label>
                  <Input value={card.contact.email} onChange={(v) => setCard({ ...card, contact: { ...card.contact, email: v } })} placeholder="contact@company.com" />
                </div>
                <div className="col-span-2">
                  <label className="text-sm text-gray-500 block mb-2">企业地址</label>
                  <Input value={card.contact.address} onChange={(v) => setCard({ ...card, contact: { ...card.contact, address: v } })} placeholder="公司详细地址" />
                </div>
              </div>
            </SectionCard>

            {/* 电子名片展示信息 */}
            <SectionCard title="电子名片展示信息" icon={User}>
              <p className="text-xs text-gray-500 mb-3">选择在电子名片上需要展示的信息字段：职位、手机号、邮箱、公司、地址</p>
              <div className="grid grid-cols-3 gap-3">
                {displayFieldOptions.map(opt => {
                  const Icon = opt.icon;
                  const checked = card.displayFields[opt.value as keyof DisplayFields];
                  const isRequired = opt.value === 'name';
                  return (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        checked
                          ? isRequired
                            ? 'border-[#1c5bff] bg-blue-50 cursor-not-allowed opacity-80'
                            : 'border-[#1c5bff] bg-blue-50 cursor-pointer'
                          : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                      }`}
                    >
                      <Checkbox checked={checked} disabled={isRequired} onChange={() => toggleDisplayField(opt.value as keyof DisplayFields)} />
                      <Icon className={`w-4 h-4 ${checked ? 'text-[#1c5bff]' : 'text-gray-400'}`} />
                      <span className={`text-sm ${checked ? 'text-[#1c5bff] font-medium' : 'text-gray-600'}`}>
                        {opt.label}
                        {isRequired && <span className="text-[10px] text-red-400 ml-1">必选</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </SectionCard>

            {/* 电子名片背景图 */}
            <SectionCard title="电子名片背景图" icon={CreditCard}>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500 mb-2">选择背景图片（预设/自定义，点击选择）</div>
                  <div className="grid grid-cols-3 gap-4">
                    {/* 预设背景图 */}
                    {DEFAULT_BACKGROUND_LIST.map((bg, idx) => (
                      <div
                        key={`preset-${idx}`}
                        onClick={() => selectBg(bg)}
                        className={`rounded-xl overflow-hidden border-2 cursor-pointer transition-all ${
                          card.cardBg === bg ? 'border-[#1c5bff] ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="relative" style={{ paddingTop: '50%' }}>
                          <img src={bg} alt={`预设背景${idx + 1}`} className="absolute inset-0 w-full h-full object-cover" />
                          {card.cardBg === bg && (
                            <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#1c5bff] flex items-center justify-center text-white text-xs">
                              ✓
                            </div>
                          )}
                        </div>
                        <div className="text-center text-xs py-2 bg-gray-50 text-gray-600">预设背景 {idx + 1}</div>
                      </div>
                    ))}
                    {/* 自定义上传的背景图 */}
                    {customBgList.map((bg, idx) => (
                      <div
                        key={`custom-${idx}`}
                        onClick={() => selectBg(bg)}
                        className={`rounded-xl overflow-hidden border-2 cursor-pointer transition-all relative ${
                          card.cardBg === bg ? 'border-[#1c5bff] ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="relative" style={{ paddingTop: '50%' }}>
                          <img src={bg} alt={`自定义背景${idx + 1}`} className="absolute inset-0 w-full h-full object-cover" />
                          {card.cardBg === bg && (
                            <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#1c5bff] flex items-center justify-center text-white text-xs">
                              ✓
                            </div>
                          )}
                          <button
                            onClick={(e) => removeCustomBg(bg, e)}
                            className="absolute top-2 left-2 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center text-white text-xs hover:bg-red-600 transition-colors"
                            title="删除"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-center text-xs py-2 bg-gray-50 text-gray-600">自定义背景 {idx + 1}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-100">
                  <label className="text-sm text-gray-500 block mb-2">上传自定义背景图片</label>
                  <ProductImageUploader
                    value=""
                    onChange={(v) => handleCustomBgUpload(v as string)}
                    simple
                  />
                  <div className="text-xs text-gray-400 mt-1">
                    建议尺寸：710rpx × 400rpx（宽高比约 1.775:1）
                    {customBgList.length > 0 && <span className="ml-2 text-[#1c5bff]">已上传 {customBgList.length} 张自定义背景</span>}
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* 名片布局样式 */}
            <SectionCard title="名片布局样式" icon={Monitor}>
              <p className="text-xs text-gray-500 mb-3">选择电子名片的内容摆放布局，会传一个字段，延用之前的contentPosition字段</p>
              <div className="grid grid-cols-4 gap-3">
                {cardLayouts.map(layout => (
                  <div
                    key={layout.value}
                    onClick={() => setCard({ ...card, cardLayout: layout.value })}
                    className={`cursor-pointer transition-all ${card.cardLayout === layout.value ? 'ring-2 ring-[#1c5bff] rounded-lg' : ''}`}
                  >
                    <CardLayoutPreview layout={layout.value} selected={card.cardLayout === layout.value} />
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* 功能配置 */}
            {/* <SectionCard title="功能配置" icon={CreditCard}>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <Switch value={card.saveToContacts} onChange={(v) => setCard({ ...card, saveToContacts: v })} />
                    <div>
                      <div className="text-sm font-medium text-gray-700">存入通讯录</div>
                      <div className="text-xs text-gray-400 mt-0.5">开启后，用户可一键将名片保存到手机通讯录</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <Switch value={card.enablePoster} onChange={(v) => setCard({ ...card, enablePoster: v })} />
                    <div>
                      <div className="text-sm font-medium text-gray-700">生成海报</div>
                      <div className="text-xs text-gray-400 mt-0.5">开启后，可生成带电子名片的分享海报</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <Switch value={card.enableMiniCode} onChange={(v) => setCard({ ...card, enableMiniCode: v })} />
                    <div>
                      <div className="text-sm font-medium text-gray-700">小程序码</div>
                      <div className="text-xs text-gray-400 mt-0.5">开启后，名片会生成小程序码供用户扫码</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <Switch value={card.enableShortLink} onChange={(v) => setCard({ ...card, enableShortLink: v })} />
                    <div>
                      <div className="text-sm font-medium text-gray-700">短链接</div>
                      <div className="text-xs text-gray-400 mt-0.5">开启后，自动生成短链接用于分享</div>
                    </div>
                  </div>
                </div>
              </div>
            </SectionCard> */}

            {/* 状态配置 */}
            <SectionCard title="状态配置" icon={Monitor}>
              <div className="flex items-center gap-4">
                <Switch value={card.status === 'active'} onChange={(v) => setCard({ ...card, status: v ? 'active' : 'inactive' })} />
                <span className="text-sm text-gray-600">
                  {card.status === 'active' ? '已启用' : '已停用'}
                </span>
              </div>
            </SectionCard>

            {/* 自定义选择配置页面内容 */}
            <SectionCard title="自定义选择配置" icon={FileText}>
              <p className="text-xs text-gray-500 mb-3">企业信息自定义选择配置展示</p>

              {/* 选择区域 */}
              <div className="mb-4">
                <div className="text-xs text-gray-600 mb-2 font-medium">可选内容</div>
                <div className="grid grid-cols-6 gap-3">
                  {availablePageOptions.map(option => {
                    const isSelected = card.selectedPages?.includes(option.value);
                    const IconComp = option.icon;
                    return (
                      <label
                        key={option.value}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? 'border-[#1c5bff] bg-blue-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => togglePageContent(option.value)}
                        />
                        <IconComp className={`w-4 h-4 ${isSelected ? 'text-[#1c5bff]' : 'text-gray-400'}`} />
                        <span className={`text-sm ${isSelected ? 'text-[#1c5bff] font-medium' : 'text-gray-700'}`}>
                          {option.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Tabs 排列区域 */}
              {card.selectedPages && card.selectedPages.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-gray-600 font-medium">
                      已选内容 ({card.selectedPages.length} 项)
                    </div>
                    <span className="text-[10px] text-gray-400">支持拖拽排序、滑动切换、点击删除</span>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <Tabs
                      value={activeTab || card.selectedPages[0]}
                      onChange={(val) => setActiveTab(val as string)}
                      dragSort
                      scrollPosition="auto"
                      theme="card"
                      size="medium"
                      onDragSort={handleTabDragSort}
                      onRemove={handleTabRemove}
                      className="custom-tabs"
                    >
                      {card.selectedPages.map((pageValue) => {
                        const option = pageContentOptions.find(o => o.value === pageValue);
                        if (!option) return null;
                        const IconComp = option.icon;

                        const tabLabel = (
                          <span className="inline-flex items-center gap-1.5 py-1">
                            <IconComp className="w-3.5 h-3.5 text-[#1c5bff]" />
                            <span className="text-xs font-medium">{option.label}</span>
                          </span>
                        );

                        return (
                          <Tabs.TabPanel
                            key={pageValue}
                            value={pageValue}
                            label={tabLabel}
                            removable
                          >
                            {renderTabPanelContent(pageValue)}
                          </Tabs.TabPanel>
                        );
                      })}
                    </Tabs>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50/50">
                  暂未选择任何内容，请在上方选择需要展示的页面
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      );
    }

    // 展示模式
    return (
      <div>
        <div className="space-y-4">
          <SectionCard title="基础信息" icon={Image}>
            {/* <DisplayRow label="名片名称" value={card.name} /> */}
            <DisplayRow label="主题" value={card.mainTitle} />
            <DisplayRow label="副题" value={card.subTitle} />
            <DisplayRow label="副题英文" value={card.subTitleEn} />
            <div className="flex items-center justify-between py-2 border-b border-gray-50">
              <span className="text-sm text-gray-500">企业Logo</span>
              <span className="text-sm text-gray-800 font-medium text-right max-w-[60%]">
                {card.logo ? (
                  <PreviewableImage src={card.logo} alt="Logo" className="h-16 object-cover rounded-lg border" />
                ) : '-'}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 last:border-0">
              <span className="text-sm text-gray-500">页面背景大背景图片</span>
              <span className="text-sm text-gray-800 font-medium text-right max-w-[60%]">
                {card.pageBg ? (
                  <PreviewableImage src={card.pageBg} alt="页面背景" className="w-24 h-14 rounded-lg border" />
                ) : '-'}
              </span>
            </div>
          </SectionCard>

          <SectionCard title="联系方式" icon={Phone}>
            <div className="flex items-center justify-between py-2 border-b border-gray-50">
              <span className="text-sm text-gray-500">企业二维码</span>
              <span className="text-sm text-gray-800 font-medium text-right max-w-[60%]">
                {card.contact.qrcode ? (
                  <PreviewableImage src={card.contact.qrcode} alt="二维码" className="w-16 h-16 object-cover rounded-lg border" />
                ) : '-'}
              </span>
            </div>
            <DisplayRow label="企业电话" value={card.contact.phone} />
            <DisplayRow label="企业邮箱" value={card.contact.email} />
            <DisplayRow label="企业地址" value={card.contact.address} />
          </SectionCard>

          <SectionCard title="电子名片展示信息" icon={User}>
            <div className="flex flex-wrap gap-2">
              {displayFieldOptions.map(opt => {
                const checked = card.displayFields[opt.value as keyof DisplayFields];
                const Icon = opt.icon;
                return (
                  <span
                    key={opt.value}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs ${
                      checked ? 'bg-blue-50 text-[#1c5bff] border border-[#1c5bff]/30' : 'bg-gray-50 text-gray-400 border border-gray-200 line-through'
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    {opt.label}
                  </span>
                );
              })}
            </div>
          </SectionCard>

          <SectionCard title="电子名片背景图" icon={CreditCard}>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-500">背景图片</span>
              <span className="text-sm text-gray-800 font-medium text-right max-w-[60%]">
                {card.cardBg ? (
                  <PreviewableImage src={card.cardBg} alt="名片背景" className="w-28 h-16 object-cover rounded-lg border" />
                ) : '默认白色背景'}
              </span>
            </div>
          </SectionCard>

          <SectionCard title="名片布局样式" icon={Monitor}>
            <DisplayRow label="当前布局" value={cardLayouts.find(l => l.value === card.cardLayout)?.label || '-'} />
          </SectionCard>

          {/* <SectionCard title="功能配置" icon={CreditCard}>
            <DisplayRow label="存入通讯录" value={card.saveToContacts ? '已开启' : '未开启'} />
            <DisplayRow label="生成海报" value={card.enablePoster ? '已开启' : '未开启'} />
            <DisplayRow label="小程序码" value={card.enableMiniCode ? '已开启' : '未开启'} />
            <DisplayRow label="短链接" value={card.enableShortLink ? '已开启' : '未开启'} />
          </SectionCard> */}

          <SectionCard title="状态" icon={Monitor}>
            <DisplayRow label="当前状态" value={card.status === 'active' ? '已启用' : '已停用'} />
          </SectionCard>

          <SectionCard title="自定义选择配置" icon={FileText}>
            {(card.selectedPages || []).length === 0 ? (
              <div className="text-center py-4 text-gray-400 text-sm">暂未选择任何展示内容</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {card.selectedPages.map((pageValue, idx) => {
                  const option = pageContentOptions.find(o => o.value === pageValue);
                  if (!option) return null;
                  const IconComp = option.icon;
                  return (
                    <span key={pageValue} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-[#1c5bff] border border-[#1c5bff]/30 text-xs">
                      <span className="w-4 h-4 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center">{idx + 1}</span>
                      <IconComp className="w-3 h-3" />
                      {option.label}
                    </span>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    );
  };

  const showReadOnlyActions = !isEditing && hasData;

  return (
    <ImagePreviewProvider>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-[#1c5bff]" />
            电子名片配置
          </h1>
          {showReadOnlyActions && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handlePreview}>
                <span className="flex items-center gap-1"><Eye className="w-4 h-4" />预览</span>
              </Button>
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] transition-colors flex items-center gap-2 text-sm"
              >
                <Edit3 className="w-4 h-4" />
                编辑
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1c5bff] mr-3"></div>
            加载中...
          </div>
        ) : (
          <div className="space-y-4">
            {renderContent()}

            {isEditing && (
              <div className="sticky bottom-0 bg-white border-t border-gray-100 rounded-xl shadow-sm p-4 flex items-center justify-end gap-3">
                <button
                  onClick={handlePreview}
                  className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  预览
                </button>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  取消
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {card.id ? '更新' : '保存'}
                </button>
              </div>
            )}
          </div>
        )}

        <Dialog
          header="电子名片预览"
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          footer={false}
          width={420}
        >
          <div className="flex flex-col items-center gap-3 py-2">
            <BusinessCardPreview config={previewConfig || card} />
          </div>
        </Dialog>

        <style>{`
          .custom-tabs .t-tabs__nav-item {
            padding: 8px 14px;
            border-radius: 8px 8px 0 0;
            transition: all 0.2s ease;
            gap: 4px;
            cursor: grab;
          }
          .custom-tabs .t-tabs__nav-item:active {
            cursor: grabbing;
          }
          .custom-tabs .t-tabs__nav-item:hover {
            background-color: #f5f5f5;
          }
          .custom-tabs .t-tabs__nav-item.t-is-active {
            font-weight: 600;
            color: #1c5bff;
            border-bottom: 2px solid #1c5bff;
          }
          .custom-tabs .t-tabs__nav-item.t-is-dragging {
            opacity: 0.5;
            background-color: #e3f2fd;
            box-shadow: 0 4px 12px rgba(28, 91, 255, 0.2);
          }
          .custom-tabs .t-tabs__nav-item .t-icon-close {
            margin-left: 4px;
            font-size: 12px;
          }
          .custom-tabs .t-tabs__nav-item .t-icon-close:hover {
            color: #ff4d4f;
          }
          .custom-tabs .t-tabs__content {
            padding-top: 12px;
          }
          .custom-tabs .t-tabs__nav--card .t-tabs__nav-item {
            border: 1px solid #e5e7eb;
            border-bottom: none;
            background: #fafafa;
          }
          .custom-tabs .t-tabs__nav--card .t-tabs__nav-item.t-is-active {
            background: #ffffff;
            border-color: #1c5bff;
            color: #1c5bff;
          }
          .custom-tabs .t-tabs__nav--card .t-tabs__nav-item.t-is-dragging {
            background: #e3f2fd;
            border-color: #1c5bff;
          }
          .custom-tabs .t-tabs__nav--card + .t-tabs__content {
            border: 1px solid #e5e7eb;
            border-top: none;
            border-radius: 0 0 8px 8px;
            background: #ffffff;
          }
        `}</style>
      </div>
    </ImagePreviewProvider>
  );
}
